import { baseUrl } from "./url";
import { toast } from "react-toastify";

const triggerLogoutConfirmation = (message, deviceInfo = null) => {
    const logoutEvent = new CustomEvent('logout_confirmation_needed', {
        detail: {
            message: message || 'Your session has been terminated due to security reasons.',
            statusCode: 403,
            deviceInfo: deviceInfo
        }
    });
    window.dispatchEvent(logoutEvent);
};

const triggerSessionExpired = (message = null) => {
    const sessionEvent = new CustomEvent('session_expired', {
        detail: {
            message: message || 'Session expired. Please login again.',
            statusCode: 401
        }
    });
    window.dispatchEvent(sessionEvent);
};

function handleUnauthorizedError(statusCode = 401, message = null, deviceInfo = null) {
    const isLoginPage =
        window.location.pathname.includes('/login') ||
        window.location.pathname.includes('/signin');

    if (statusCode === 403) {
        sessionStorage.clear();
        localStorage.removeItem('authToken');

        if (!isLoginPage) {
            triggerLogoutConfirmation(message, deviceInfo);
        }
        return;
    }

    if (statusCode === 401) {
        localStorage.removeItem('authToken');
        
        if (!isLoginPage) {
            triggerSessionExpired(message);
        }
        return;
    }
}

export async function postRequest(url, body = {}) {
    try {
        const token = localStorage.getItem('authToken');
        const isFormData = body instanceof FormData;

        const headers = {};

        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }

        if (!isFormData) {
            headers["Content-Type"] = "application/json";
        }

        const response = await fetch(`${baseUrl}${url}`, {
            method: 'POST',
            headers,
            body: isFormData ? body : JSON.stringify(body),
        });
        
        let responseData = null;
        try {
            responseData = await response.clone().json();
        } catch (e) {}

        if (response.status === 401 || response.status === 403) {
            let errorMessage = response.status === 403 ? 'Session terminated' : 'Session expired';
            let deviceInfo = null;

            try {
                const err = responseData || await response.clone().json();
                errorMessage = err.message || err.msg || errorMessage;
                deviceInfo = err.deviceInfo || null;
            } catch {
                try {
                    errorMessage = await response.clone().text();
                } catch (e) {
                }
            }

            // Only show the "Session Expired" modal if the user actually had a token that became invalid.
            // If they never had a token, they are just a guest hitting a protected route (or the backend incorrectly protects a public route).
            if (token) {
                handleUnauthorizedError(response.status, errorMessage, deviceInfo);
            }
            throw new Error(errorMessage);
        }

        if (!response.ok) {
            throw new Error(`POST ${url} failed with status ${response.status}`);
        }

        return await response.json();

    } catch (error) {
        console.error("POST error:", error);
        throw error;
    }
}

export async function getRequest(url, params = {}) {
  try {
    const token = localStorage.getItem('authToken');
    // Build query string if params provided
    const queryString = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${baseUrl}${url}${queryString}`, {
      method: 'GET',
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });

    let responseData = null;
    try {
        responseData = await response.clone().json();
    } catch (e) {}

    // Handle 401 and 403 errors
    if (response.status === 401 || response.status === 403) {
      let errorMessage = response.status === 403 ? 'Session terminated' : 'Session expired';
      let deviceInfo = null;

      try {
          const err = responseData || await response.clone().json();
          errorMessage = err.message || err.msg || errorMessage;
          deviceInfo = err.deviceInfo || null;
      } catch {
          try {
              errorMessage = await response.clone().text();
          } catch (e) {
          }
      }

      if (token) {
          handleUnauthorizedError(response.status, errorMessage, deviceInfo);
      }
      throw new Error(errorMessage);
    }

    if (!response.ok) {
      throw new Error(`GET ${url} failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("GET error:", error);
    throw error;
  }
}

// Function to manually check session status
export async function checkSessionStatus() {
    try {
        const token = localStorage.getItem('authToken');
        if (!token) return { valid: false };

        const response = await fetch(`${baseUrl}/api/auth/check-session`, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });

        if (response.status === 401 || response.status === 403) {
            let errorMessage = response.status === 403 ? 'Session terminated' : 'Session expired';
            let deviceInfo = null;

            try {
                const err = await response.json();
                errorMessage = err.message || errorMessage;
                deviceInfo = err.deviceInfo || null;
            } catch (e) {
                // Ignore parsing error
            }

            handleUnauthorizedError(response.status, errorMessage, deviceInfo);
            return { valid: false };
        }

        if (!response.ok) {
            return { valid: false };
        }

        return { valid: true, data: await response.json() };
    } catch (error) {
        console.error("Session check error:", error);
        return { valid: false };
    }
}

// Optional: Export a function to clear all session data
export function clearSession() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.clear();
    
    // Clear cookies
    document.cookie.split(";").forEach(function(c) {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
}

// Optional: Export a function to check if user is authenticated
export function isAuthenticated() {
    const token = localStorage.getItem('authToken');
    return !!token;
}